import type {
  ConnectorOverview,
  ConnectorTool,
  ConnectorToolGroup,
  ToolAccess,
  ToolGroup,
} from "@/lib/api";
import { apiErrorMessage, isToastedError } from "@/lib/api-errors";
import { i18n } from "@/lib/i18n";
import { matchesSearch } from "@/lib/search";
import { isServed, toolsByGroup } from "@/lib/tool-groups";

/** The parts of a group, in the order the page shows them: reading first. */
const ACCESS_ORDER: readonly ToolAccess[] = ["read", "write"];

/*
 * Who sees what. The overview lists the tools the viewer gets, by their role
 * (the backend decides): a developer every tool, an owner and an admin the
 * catalogue with the tools of their role, a member the tools of their teams
 * and exceptions. Everyone sees each tool's technical name, whether it reads
 * or writes, whether a developer paused it for everyone, and their own switch
 * ("Voor mij"). The admin roles also see who each tool is for ("Voor", one
 * level of the overview's `audience`), and a developer pauses a tool for
 * everyone in its dialog.
 */

/** A tool's kind, as the overview says it. */
export type ToolKind = ConnectorTool["kind"];

/** The kinds in the order the kind filter lists them: Odoo first, the viewer's own last. */
export const TOOL_KINDS: readonly ToolKind[] = ["odoo", "account", "submission", "role"];

/** The kinds among the tools, in `TOOL_KINDS` order, for the kind filter. */
export function listedKinds(tools: readonly Pick<ConnectorTool, "kind">[]): ToolKind[] {
  return TOOL_KINDS.filter((kind) => tools.some((tool) => tool.kind === kind));
}

/**
 * The groups the page knows, for its group filter and the URL's: those with
 * a tool the overview lists, in the catalogue's order.
 */
export function listedGroups<G extends Pick<ConnectorToolGroup, "id">>(
  groups: readonly G[],
  tools: readonly Pick<ConnectorTool, "group">[],
): G[] {
  return groups.filter((group) => tools.some((tool) => tool.group === group.id));
}

/** A column of the tools' table. */
export type ToolColumn = "tool" | "audience" | "personal";

/**
 * The table's columns, in order: the tool, for the admin roles who it is for
 * ("Voor"), and the viewer's own switch ("Voor mij"), last.
 */
export function toolColumns(isAdmin: boolean): readonly ToolColumn[] {
  return isAdmin ? ["tool", "audience", "personal"] : ["tool", "personal"];
}

/** The texts the search looks in: a tool's title and summary (in the dashboard's language) and its technical name. */
export function toolSearchTexts(tool: Pick<ConnectorTool, "name" | "title" | "summary">): string[] {
  return [tool.title, tool.summary, tool.name];
}

/**
 * What the viewer gets of a group, for its header row: the tools Claude can
 * use (`tools`), of which `read` read and `write` write, and the tools that
 * are coming soon (`soon`).
 */
export interface GroupCounts {
  tools: number;
  read: number;
  write: number;
  soon: number;
}

/** A group on the Tools page: its title, its summary, its tools and what the viewer gets of it. */
export interface ToolGroupSection extends ConnectorToolGroup {
  /**
   * The group's tools: the read ones, then the write ones, each in the
   * catalogue's order (the available ones, then the coming-soon ones). After
   * `filterToolSections`, only those that match.
   */
  tools: ConnectorTool[];
  /** What the viewer gets of the whole group, whatever the filters (`GroupCounts`). */
  counts: GroupCounts;
}

/** What the viewer gets of these tools (`GroupCounts`). */
export function groupCounts(
  tools: readonly Pick<ConnectorTool, "access" | "status">[],
): GroupCounts {
  const served = tools.filter(isServed);
  return {
    tools: served.length,
    read: served.filter((tool) => tool.access === "read").length,
    write: served.filter((tool) => tool.access === "write").length,
    soon: tools.length - served.length,
  };
}

/**
 * A group row's counts in words: "6 tools, 5 lezen, 1 schrijven", then "2
 * binnenkort" only when there are any. A count of none (no write tools, or no
 * tool Claude can use yet) is left out.
 */
export function groupCountsText(counts: GroupCounts): string {
  const parts = [
    counts.tools > 0 && i18n.t("tools.groupCount.tools", { count: counts.tools }),
    counts.read > 0 && i18n.t("tools.groupCount.read", { count: counts.read }),
    counts.write > 0 && i18n.t("tools.groupCount.write", { count: counts.write }),
    counts.soon > 0 && i18n.t("tools.groupCount.soon", { count: counts.soon }),
  ];
  return parts.filter((part): part is string => typeof part === "string").join(", ");
}

/**
 * The tools under their group, in the catalogue's group order, each group's
 * read tools first, then its write tools. A group without tools is left out.
 */
export function toolGroupSections(
  groups: readonly ConnectorToolGroup[],
  tools: ConnectorTool[],
): ToolGroupSection[] {
  return toolsByGroup(groups, tools).map(({ tools: groupTools, ...group }) => ({
    ...group,
    tools: ACCESS_ORDER.flatMap((access) => groupTools.filter((tool) => tool.access === access)),
    counts: groupCounts(groupTools),
  }));
}

/** The tool nobody can switch off, not even for themselves: `whoami` is how a person checks their sign-in. */
const ALWAYS_ON_TOOLS: ReadonlySet<string> = new Set(["whoami"]);

/**
 * Whether the viewer can switch this tool off and on for themselves: every
 * available tool they get except `whoami`. A coming-soon tool is not served,
 * so it has no switch.
 */
export function isPersonallySwitchable(tool: Pick<ConnectorTool, "name" | "status">): boolean {
  return isServed(tool) && !ALWAYS_ON_TOOLS.has(tool.name);
}

/**
 * Why a tool that is not paused still does not reach Claude: the connector is
 * off, a role's tool or a submission needs Microsoft sign-in, the tool writes
 * where the backend only reads (every environment but production), Odoo is
 * not connected, or (for an occupancy tool, computed with the Bezettingsgraad
 * app's rules) that app is off.
 */
export type UnavailableReason =
  "connectorOff" | "needsSignIn" | "writesOff" | "odooNotConnected" | "appOff";

/** What a tool needs from the connector to be served, for the reasons it is not. */
export interface Serving {
  /** How the connector runs: says why a tool that is switched on still does not reach Claude. */
  mode: ConnectorOverview["mode"];
  /** Whether the backend may write to third-party systems (`external_writes`). */
  externalWrites: boolean;
  /** Whether the connector serves the Odoo tools (`odoo_tools_on`). */
  odooToolsOn: boolean;
}

/**
 * The "Voor mij" column, the viewer's own switch, for everyone:
 * - `switch`: their switch, with why Claude does not get the tool anyway
 *   when the connector does not serve it (a paused tool says so before its
 *   summary, with "Tijdelijk uit");
 * - `alwaysOn`: `whoami`, which nobody switches off, with the same reason;
 * - `comingSoon`: not served yet.
 */
export type PersonalState =
  | { kind: "switch"; unavailable: UnavailableReason | null }
  | { kind: "alwaysOn"; unavailable: UnavailableReason | null }
  | { kind: "comingSoon" };

type StateTool = Pick<ConnectorTool, "name" | "kind" | "access" | "status" | "on" | "paused">;

/** The "Voor mij" column of a tool (`PersonalState`). */
export function personalState(tool: StateTool, serving: Serving): PersonalState {
  if (!isServed(tool)) {
    return { kind: "comingSoon" };
  }
  // A paused tool is off for everyone: its "Tijdelijk uit" says why, never a reason here.
  const unavailable = tool.on || tool.paused ? null : unavailableReason(tool, serving);
  return isPersonallySwitchable(tool)
    ? { kind: "switch", unavailable }
    : { kind: "alwaysOn", unavailable };
}

/**
 * Why the connector does not serve a tool, in the dashboard's language, as
 * the row's "Niet beschikbaar" and the tool's dialog say it.
 */
export function unavailableReasonText(reason: UnavailableReason): string {
  return i18n.t(`tools.unavailableReason.${reason}`, { app: i18n.t("apps.bezetting.title") });
}

/**
 * Why a tool that is not paused is not served: the connector is off; else a
 * role's tool or a submission, which only Microsoft sign-in serves; else a
 * tool of the catalogue that writes where writes are off, whatever its kind
 * and whether Odoo is connected, as connecting Odoo would not serve it; else
 * an account tool goes with the connector, and an Odoo tool waits for Odoo.
 * An Odoo tool that is off while Odoo is connected can only wait for its app:
 * the server serves the occupancy tools only while the Bezettingsgraad app is
 * on.
 */
function unavailableReason(
  tool: Pick<ConnectorTool, "kind" | "access">,
  { mode, externalWrites, odooToolsOn }: Serving,
): UnavailableReason {
  if (mode === "off") {
    return "connectorOff";
  }
  if (tool.kind === "role" || tool.kind === "submission") {
    return "needsSignIn";
  }
  if (tool.access === "write" && !externalWrites) {
    return "writesOff";
  }
  if (tool.kind === "account") {
    return "connectorOff";
  }
  return odooToolsOn ? "appOff" : "odooNotConnected";
}

/**
 * What a tool's row or dialog says when switching it failed, in the
 * dashboard's language: the server's code when it sent one (`tool_not_found`,
 * `tool_not_switchable`, `developer_required`, `service_unavailable`, a
 * session code), else that the tool could not be switched on or off and to
 * try again; nothing when a toast already says it (`isToastedError`). For the
 * developers' switch and the viewer's own alike.
 */
export function switchErrorMessage(error: unknown, enabled: boolean): string | null {
  return isToastedError(error)
    ? null
    : apiErrorMessage(error, enabled ? "switchToolOn" : "switchToolOff");
}

/**
 * A parameter's default as Claude reads it in the schema: its JSON (`50`,
 * `"open"`, `true`), a list with a space after each comma (`["read",
 * "write"]`) so it can wrap. The backend sends null for none.
 */
export function defaultText(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  return Array.isArray(value)
    ? `[${value.map((item) => JSON.stringify(item)).join(", ")}]`
    : JSON.stringify(value);
}

/** Which tools the Tools page shows by access: all, or only the read or the write ones. */
export type AccessFilter = "all" | ToolAccess;

/** Which tools the Tools page shows by kind: all, or one kind. */
export type KindFilter = "all" | ToolKind;

/** The Tools page's filters: access, kind, the groups (none means every group) and a search text. */
export interface ToolFilters {
  access: AccessFilter;
  kind: KindFilter;
  groups: ToolGroup[];
  search: string;
}

export const NO_TOOL_FILTERS: ToolFilters = {
  access: "all",
  kind: "all",
  groups: [],
  search: "",
};

export const ACCESS_FILTERS: readonly AccessFilter[] = ["all", "read", "write"];

/** The filters' keys in the page's URL, so a filtered view can be shared. */
const PARAM = { access: "access", kind: "kind", groups: "group", search: "q" } as const;

/**
 * A choice of groups as the filter keeps it: in the catalogue's order, each
 * once, only groups the catalogue lists, and none when it holds them all
 * (every group is no filter).
 */
export function chosenGroups(
  chosen: readonly string[],
  groups: readonly ConnectorToolGroup[],
): ToolGroup[] {
  const ids = groups.filter((group) => chosen.includes(group.id)).map((group) => group.id);
  return ids.length === groups.length ? [] : ids;
}

/**
 * The filters in the page's URL (`?access=write&kind=odoo&group=clients,cranes&q=offerte`).
 * An access it does not know, or a kind or a group the page does not list,
 * counts as no filter.
 */
export function parseToolFilters(
  params: URLSearchParams,
  groups: readonly ConnectorToolGroup[],
  kinds: readonly ToolKind[],
): ToolFilters {
  const access = params.get(PARAM.access);
  const kind = params.get(PARAM.kind);
  return {
    access: ACCESS_FILTERS.find((known) => known === access) ?? "all",
    kind: kinds.find((known) => known === kind) ?? "all",
    groups: chosenGroups((params.get(PARAM.groups) ?? "").split(","), groups),
    search: params.get(PARAM.search) ?? "",
  };
}

/** The filters as URL parameters, leaving out every filter that is not set. */
export function toolFilterParams(filters: ToolFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters.access !== "all") {
    params[PARAM.access] = filters.access;
  }
  if (filters.kind !== "all") {
    params[PARAM.kind] = filters.kind;
  }
  if (filters.groups.length > 0) {
    params[PARAM.groups] = filters.groups.join(",");
  }
  if (filters.search.trim() !== "") {
    params[PARAM.search] = filters.search;
  }
  return params;
}

/** Whether any filter is set: the page then offers to reset them, and opens every group with a match. */
export function hasToolFilters(filters: ToolFilters): boolean {
  return Object.keys(toolFilterParams(filters)).length > 0;
}

/**
 * What the group filter's button says: "Alle groepen" for none, the titles of
 * one or two groups ("Klanten, Kranen"), else how many ("3 groepen").
 */
export function groupsFilterLabel(
  chosen: readonly ToolGroup[],
  groups: readonly ConnectorToolGroup[],
): string {
  if (chosen.length === 0) {
    return i18n.t("tools.filter.allGroups");
  }
  if (chosen.length > 2) {
    return i18n.t("tools.filter.groupCount", { count: chosen.length });
  }
  return groups
    .filter((group) => chosen.includes(group.id))
    .map((group) => group.title)
    .join(", ");
}

/**
 * Whether a tool matches the filters. The search finds every word of it in
 * the tool's title, summary or technical name (`toolSearchTexts`), in any
 * order.
 */
export function matchesToolFilters(
  tool: Pick<ConnectorTool, "name" | "title" | "summary" | "group" | "access" | "kind">,
  filters: ToolFilters,
): boolean {
  if (filters.access !== "all" && tool.access !== filters.access) {
    return false;
  }
  if (filters.kind !== "all" && tool.kind !== filters.kind) {
    return false;
  }
  if (filters.groups.length > 0 && !filters.groups.includes(tool.group)) {
    return false;
  }
  return matchesSearch(toolSearchTexts(tool), filters.search);
}

/**
 * The groups with only the tools that match the filters. A group without a
 * match is left out; a group keeps its counts of the whole group.
 */
export function filterToolSections(
  sections: readonly ToolGroupSection[],
  filters: ToolFilters,
): ToolGroupSection[] {
  return sections
    .map((section) => ({
      ...section,
      tools: section.tools.filter((tool) => matchesToolFilters(tool, filters)),
    }))
    .filter((section) => section.tools.length > 0);
}

/**
 * Which groups are open: every group, but those the viewer folded. With no
 * filter set, those folded without a filter (`folded`), which stay folded
 * after a filter is cleared; with a filter set, those folded while that
 * filter held (`foldedUnderFilter`), so another filter opens every match
 * again.
 */
export function isGroupOpen(
  group: ToolGroup,
  filtered: boolean,
  folded: ReadonlySet<ToolGroup>,
  foldedUnderFilter: ReadonlySet<ToolGroup>,
): boolean {
  return !(filtered ? foldedUnderFilter : folded).has(group);
}
